import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { mapTenantToCurrencyCode } from '@rma/generic/core/instance';
import { Environment } from '@rma/public/generic/util/environment';
import { filter } from 'rxjs';
import {
  META_PIXEL_STANDARD_EVENTS,
  MetaEventCustomParams,
  MetaEventOptions,
  MetaPixelStandardEvents,
  MetaStandardEventParams,
  RmaCustomEventParams,
} from '../domain/fb-events.model';
import { MetaPixelService } from '../util-meta-pixel/meta-pixel.service';

interface MetaPixelStandardRouteEvent {
  type?: 'standard';
  event: MetaPixelStandardEvents;
  params?: MetaStandardEventParams[MetaPixelStandardEvents];
  options?: MetaEventOptions;
  customData?: MetaEventCustomParams<MetaPixelStandardEvents>;
}

interface MetaPixelCustomRouteEvent {
  type: 'custom';
  event: string;
  params?: RmaCustomEventParams & { [key: string]: unknown };
  options?: MetaEventOptions;
}

export type MetaPixelRouteEvent = MetaPixelStandardRouteEvent | MetaPixelCustomRouteEvent;

@Injectable({ providedIn: 'root' })
export class MetaPixelRouteTrackingService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly environment = inject(Environment);
  private readonly pixel = inject(MetaPixelService);
  private readonly router = inject(Router);

  public init(): void {
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((e) => e instanceof NavigationEnd),
      )
      .subscribe(() => {
        this.pixel.track('PageView');

        const route = this.getDeepestChild(this.router.routerState.snapshot.root);
        const rawRouteData = route.data?.['metaPixelEvents'];

        if (!rawRouteData) {
          return;
        }

        const events = this.normalizeEvents(rawRouteData);

        events?.map((e) => this.appendAdditionalDetails(e)).forEach((e) => this.trackEvent(e));
      });
  }

  // Some details cannot be added by route data
  private appendAdditionalDetails(e: MetaPixelRouteEvent) {
    switch (e.event) {
      case 'CompleteRegistration': {
        e.params = {
          ...(e.params ?? {}),
          currency: mapTenantToCurrencyCode(this.environment.tenantCode),
        };
        return e;
      }
    }
    return e;
  }

  private trackEvent(e: MetaPixelRouteEvent) {
    if (this.isCustomEvent(e)) {
      this.pixel.trackCustom(e.event, e.params, e.options);
      return;
    }

    if (!this.isKnownStandardEvent(e.event)) {
      console.error(`[MetaPixel] Unknown standard event: "${e.event}"`);
      return;
    }

    this.pixel.track(e.event, e.params, e.options, e.customData);
  }

  private isKnownStandardEvent(event: string): event is MetaPixelStandardEvents {
    // SAFETY(legacy): unreviewed, pre-dates this rule.
    return META_PIXEL_STANDARD_EVENTS.includes(event as MetaPixelStandardEvents);
  }

  private isCustomEvent(e: MetaPixelRouteEvent): e is MetaPixelCustomRouteEvent {
    return e.type === 'custom';
  }

  private getDeepestChild(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  private normalizeEvents(raw: MetaPixelRouteEvent | MetaPixelRouteEvent[]): MetaPixelRouteEvent[] | undefined {
    if (!raw) {
      return;
    }
    return Array.isArray(raw) ? raw : [raw];
  }
}
